import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Menu, X } from "lucide-react";
import { Toaster } from "./ui/sonner";
import { Helmet } from "react-helmet";

const LOGO_SMALL = "/assets/logo_small.png";

const fallbackPlayers = [
  {
    athlete_shortname: "TERRACCIANO P.",
    club_name: "Milan",
    apps: 2,
    av_fx_pts: 5.0,
    pos_short: "GK"
  },
  {
    athlete_shortname: "PESSINA M.",
    club_name: "Bologna",
    apps: 1,
    av_fx_pts: 5.0,
    pos_short: "MID"
  },
  {
    athlete_shortname: "MARTINEZ L.",
    club_name: "Inter",
    apps: 21,
    av_fx_pts: 4.88,
    pos_short: "ATT"
  },
  {
    athlete_shortname: "THURAM M.",
    club_name: "Inter",
    apps: 15,
    av_fx_pts: 4.43,
    pos_short: "ATT"
  },
  {
    athlete_shortname: "BELOTTI A.",
    club_name: "Cagliari",
    apps: 3,
    av_fx_pts: 4.33,
    pos_short: "ATT"
  },
  {
    athlete_shortname: "PULISIC C.",
    club_name: "Milan",
    apps: 14,
    av_fx_pts: 4.32,
    pos_short: "MID"
  }
];

// Translations
const pageContent = {
  it: {
    nav: {
      signIn: "Accedi",
      register: "Registrati",
      menu: {
        rules: "Regolamento",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Prezzi Giocatori",
        contact: "Contatti"
      }
    },
    footer: {
      rights: "Tutti i diritti riservati.",
      contact: "Domande o feedback?"
    },
    meta: {
      title: "Top Players | Fanteex",
      description: "Scopri i migliori giocatori di Fanteex per performance e impatto. Classifica aggiornata automaticamente con statistiche e punteggi."
    },
    page: {
      title: "Top Players",
      descriptionLines: [
        "Qui trovi i dati aggiornati sulle performance dei calciatori.",
        "Le statistiche sono calcolate su Fanteex, stagione dopo stagione.",
        "Ordina e filtra per scoprire i migliori in ogni ruolo."
      ],
      table: {
        name: "Nome",
        team: "Squadra",
        apps: "Pres",
        fxAvg: "Media FX"
      },
      filters: {
        seasonLabel: "Stagione",
        competitionLabel: "Campionato",
        competitions: {
          all: "Tutti",
          "serie-a": "Serie A",
          "coppa-italia": "Coppa Italia"
        }
      }
    }
  },
  en: {
    nav: {
      signIn: "Sign In",
      register: "Register",
      menu: {
        rules: "Game Rules",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Player Values",
        contact: "Get in Touch"
      }
    },
    footer: {
      rights: "All rights reserved.",
      contact: "Questions or feedback?"
    },
    meta: {
      title: "Top Players | Fanteex",
      description: "Discover the best Fanteex players by performance and impact. Automatically updated rankings with stats and scores."
    },
    page: {
      title: "Top Players",
      descriptionLines: [
        "Here you find the latest performance data for players.",
        "Stats are calculated on Fanteex, season after season.",
        "Sort and filter to discover the best by role."
      ],
      table: {
        name: "Name",
        team: "Team",
        apps: "Apps",
        fxAvg: "FX Avg"
      },
      filters: {
        seasonLabel: "Season",
        competitionLabel: "Competition",
        competitions: {
          all: "All",
          "serie-a": "Serie A",
          "coppa-italia": "Coppa Italia"
        }
      }
    }
  }
};

const SEASON_OPTIONS = ["2026/27"];
const COMPETITIONS = [
  { value: "all", type: "all" },
  { value: "serie-a", type: "club" },
  { value: "coppa-italia", type: "club" }
];
const COMPETITION_OPTIONS = COMPETITIONS.map((c) => c.value);

// Tech Background Component
const TechBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <div className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] bg-[#1a0b2e] rounded-full blur-[100px] opacity-80" />
    <div className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] bg-[#0f0518] rounded-full blur-[100px] opacity-90" />
    <div className="absolute inset-0 bg-[linear-gradient(rgba(93,253,203,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(93,253,203,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
  </div>
);

const TopPlayersPage = () => {
  const [lang, setLang] = useState('it');
  const [menuOpen, setMenuOpen] = useState(false);
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activePos, setActivePos] = useState("ALL");
  const [sortConfig, setSortConfig] = useState({ key: "av_fx_pts", direction: "desc" });
  const [season, setSeason] = useState(SEASON_OPTIONS[0]);
  const [competition, setCompetition] = useState(COMPETITION_OPTIONS[0]);

  const t = pageContent[lang];

  const toggleLanguage = () => {
    setLang(prev => prev === 'it' ? 'en' : 'it');
  };

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  const menuItems = [
    { label: t.nav.menu.rules, path: "/rules" },
    { label: t.nav.menu.community, path: "/community" },
    { label: t.nav.menu.topPlayers, path: "/top-players" },
    { label: t.nav.menu.playerValues, path: "/players-costs" },
    { label: t.nav.menu.contact, path: "/#contact" }
  ];

  useEffect(() => {
    let isMounted = true;

    const loadTopPlayers = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const isLocalhost = window.location.hostname === "localhost";
        const base = isLocalhost ? "http://localhost:8000" : "https://app.fanteex.com";
        const apiUrl = `${base}/api/top-players?season=${encodeURIComponent(season)}&competition=${encodeURIComponent(competition)}`;
        const response = await fetch(apiUrl);
        if (!response.ok) {
          const errorBody = await response.json().catch(() => null);
          throw new Error(errorBody?.error || `Request failed: ${response.status}`);
        }
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data?.data || []);
        if (isMounted) {
          setPlayers(list);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error?.message || "Errore di caricamento.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTopPlayers();

    return () => {
      isMounted = false;
    };
  }, [season, competition]);

  const positionTabs = [
    { key: "ALL", label: "ALL" },
    { key: "GK", label: "GK" },
    { key: "DEF", label: "DEF" },
    { key: "MID", label: "MID" },
    { key: "ATT", label: "ATT" }
  ];

  const basePlayers = loadError ? fallbackPlayers : players;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const competitionType = COMPETITIONS.find((c) => c.value === competition)?.type || "all";
  const getTeamText = (player) => {
    if (competitionType === "club") return player.club_name || "-";
    if (competitionType === "national") return player.team_name || "-";
    const club = player.club_name || "-";
    const nation = player.team_name || "-";
    return `${club}/${nation}`;
  };
  const normalizePosition = (value) => {
    if (!value) {
      return "";
    }
    const raw = String(value).trim().toUpperCase();
    const map = {
      G: "GK",
      D: "DEF",
      M: "MID",
      A: "ATT",
      F: "ATT",
      GOALKEEPER: "GK",
      PORTIERE: "GK",
      DEFENDER: "DEF",
      DIFENSORE: "DEF",
      MIDFIELDER: "MID",
      CENTROCAMPISTA: "MID",
      FORWARD: "ATT",
      STRIKER: "ATT",
      ATTACKER: "ATT",
      ATTACCANTE: "ATT"
    };
    return map[raw] || raw;
  };
  const filteredPlayers = basePlayers.filter((player) => {
    const position = normalizePosition(player.pos_short || player.position || "");
    if (activePos !== "ALL" && position !== activePos) {
      return false;
    }
    if (!normalizedSearch) {
      return true;
    }
    const name = (player.athlete_shortname || "").toLowerCase();
    const team = getTeamText(player).toLowerCase();
    return name.includes(normalizedSearch) || team.includes(normalizedSearch);
  });

  const sortDefaults = {
    name: "asc",
    team: "asc",
    apps: "desc",
    av_fx_pts: "desc"
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: sortDefaults[key] || "asc" };
    });
  };

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const direction = sortConfig.direction === "asc" ? 1 : -1;
    if (sortConfig.key === "name") {
      const nameA = (a.athlete_shortname || "").toLowerCase();
      const nameB = (b.athlete_shortname || "").toLowerCase();
      return nameA.localeCompare(nameB) * direction;
    }
    if (sortConfig.key === "team") {
      const teamA = getTeamText(a).toLowerCase();
      const teamB = getTeamText(b).toLowerCase();
      return teamA.localeCompare(teamB) * direction;
    }
    if (sortConfig.key === "apps") {
      const appsA = Number(a.apps ?? -Infinity);
      const appsB = Number(b.apps ?? -Infinity);
      return (appsA - appsB) * direction;
    }
    const fxA = Number(a.av_fx_pts ?? -Infinity);
    const fxB = Number(b.av_fx_pts ?? -Infinity);
    return (fxA - fxB) * direction;
  });

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return "↕";
    }
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const roleInitials = {
    GK: { it: "P", en: "G" },
    DEF: { it: "D", en: "D" },
    MID: { it: "C", en: "M" },
    ATT: { it: "A", en: "A" }
  };

  const getRoleInitial = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    return roleInitials[normalized]?.[lang] || "-";
  };

  const getRoleDotClass = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    const map = {
      GK: "bg-amber-400 text-black",
      DEF: "bg-sky-400 text-black",
      MID: "bg-emerald-400 text-black",
      ATT: "bg-rose-400 text-black"
    };
    return map[normalized] || "bg-white/20 text-white";
  };

  const formatFxAvg = (value) => {
    if (value === null || value === undefined) return "-";
    return Number(value).toFixed(2);
  };


  return (
    <>
      <Helmet>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href="https://fanteex.com/top-players" />
      </Helmet>

      <div className="relative min-h-screen bg-fanteex-dark overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-fanteex-dark">
        {/* Background */}
        <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
          <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/noise.svg)` }}></div>
        </div>

        <TechBackground />

        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 px-6 py-6 transition-all duration-300 backdrop-blur-md border-b border-white/5">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleMenu}
                className="text-white/70 hover:text-[#5DFDCB] transition-colors p-2"
                aria-label="Menu"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              <Link to="/" className="flex items-center gap-3 cursor-pointer group">
                <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 md:h-10 w-auto group-hover:drop-shadow-[0_0_8px_#5DFDCB] transition-all" />
              </Link>
            </div>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="https://app.fanteex.com/signin.html">
                <button className="bg-transparent border border-white/30 text-white px-4 md:px-6 py-2 rounded-full font-display font-bold uppercase text-xs md:text-sm tracking-wider hover:border-[#5DFDCB] hover:text-[#5DFDCB] transition-all duration-300">
                  {t.nav.signIn}
                </button>
              </a>
              <a href="https://app.fanteex.com/register.html" className="hidden md:block">
                <button className="bg-[#5DFDCB] text-black px-4 md:px-6 py-2 rounded-full font-display font-black uppercase text-xs md:text-sm tracking-wider hover:shadow-[0_0_20px_#5DFDCB] transition-all duration-300">
                  {t.nav.register}
                </button>
              </a>
            </div>
          </div>
        </nav>

        {/* Slide-out Menu */}
        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={toggleMenu}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              />

              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "tween", duration: 0.3 }}
                className="fixed top-0 left-0 h-full w-72 md:w-80 bg-[#0f0518]/95 backdrop-blur-xl border-r border-[#5DFDCB]/20 z-50 flex flex-col"
              >
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                  <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 w-auto" />
                  <button onClick={toggleMenu} className="text-white/70 hover:text-[#5DFDCB] transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex-1 py-6">
                  {menuItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={toggleMenu}
                      className="block px-6 py-4 text-white/80 hover:text-[#5DFDCB] hover:bg-white/5 font-display font-bold uppercase tracking-wider text-sm transition-all border-l-2 border-transparent hover:border-[#5DFDCB]"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>

                <div className="p-6 border-t border-white/10">
                  <button
                    onClick={toggleLanguage}
                    className="flex items-center gap-2 text-white/70 hover:text-[#5DFDCB] transition-colors font-display font-bold uppercase text-sm tracking-wider"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{lang === 'it' ? 'English' : 'Italiano'}</span>
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Page Content */}
        <main className="flex-1 pt-28 pb-16 px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-display font-black text-white uppercase mb-4 text-center"
            >
              {t.page.title}
            </motion.h1>
            <div className="text-center text-white/70 font-sans text-sm md:text-base mb-8 space-y-2">
              {t.page.descriptionLines.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>

            <div className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-6 mb-6">
              <div className="flex items-center gap-3 bg-[#1a0b2e] border border-[#5DFDCB]/30 rounded-xl px-4 py-3">
                <span className="text-[#5DFDCB]">🔎</span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={lang === "it" ? "Cerca nome o squadra" : "Search name or team"}
                  className="w-full bg-transparent text-white/80 font-sans focus:outline-none placeholder:text-white/40"
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4 text-sm font-display font-bold uppercase">
                  {positionTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActivePos(tab.key)}
                      className={`relative px-2 py-1 transition-colors ${
                        activePos === tab.key ? "text-white" : "text-white/50 hover:text-white/80"
                      }`}
                    >
                      {tab.label}
                      {activePos === tab.key && (
                        <span className="absolute left-0 right-0 -bottom-1 h-[2px] bg-[#ff5f2e]" />
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-[11px] font-display font-bold uppercase tracking-wider text-white/50">
                    {t.page.filters.seasonLabel}
                    <select
                      value={season}
                      onChange={(event) => setSeason(event.target.value)}
                      className="bg-[#1a0b2e] border border-[#5DFDCB]/30 rounded-lg px-3 py-1.5 text-white text-xs font-display font-bold focus:outline-none focus:border-[#5DFDCB] cursor-pointer"
                    >
                      {SEASON_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>

                  <label className="flex items-center gap-2 text-[11px] font-display font-bold uppercase tracking-wider text-white/50">
                    {t.page.filters.competitionLabel}
                    <select
                      value={competition}
                      onChange={(event) => setCompetition(event.target.value)}
                      className="bg-[#1a0b2e] border border-[#5DFDCB]/30 rounded-lg px-3 py-1.5 text-white text-xs font-display font-bold focus:outline-none focus:border-[#5DFDCB] cursor-pointer"
                    >
                      {COMPETITION_OPTIONS.map((option) => (
                        <option key={option} value={option}>{t.page.filters.competitions[option]}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-[#140623]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-6">
              {isLoading && (
                <p className="text-white/70 font-sans text-center py-8">
                  {lang === "it" ? "Caricamento classifica..." : "Loading rankings..."}
                </p>
              )}

              {!isLoading && (
                <div>
                  <div>
                    {loadError && (
                      <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[12px] text-amber-200 font-sans">
                        {lang === "it"
                          ? "Abbiamo problemi con la connessione ai dati, riprova piu tardi. Questi sono dati di test."
                          : "We are having trouble connecting to live data, please try again later. These are test data."}
                      </div>
                    )}
                    {/* Desktop/Tablet Header */}
                    <div className="hidden sm:grid grid-cols-[32px_24px_1fr_auto_56px_56px] gap-1.5 text-[11px] sm:text-xs font-display font-bold uppercase text-white/60 border-b border-white/10 pb-2">
                      <div />
                      <div />
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="text-left hover:text-white transition-colors"
                      >
                        {t.page.table.name} {renderSortIndicator("name")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("team")}
                        className="text-left hover:text-white transition-colors"
                      >
                        {t.page.table.team} {renderSortIndicator("team")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("apps")}
                        className="text-right hover:text-white transition-colors"
                      >
                        {t.page.table.apps} {renderSortIndicator("apps")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("av_fx_pts")}
                        className="text-right text-[#5DFDCB] hover:text-white transition-colors"
                      >
                        {t.page.table.fxAvg} {renderSortIndicator("av_fx_pts")}
                      </button>
                    </div>
                    {/* Mobile Header */}
                    <div className="flex sm:hidden items-center text-[13px] font-display font-bold uppercase text-white/60 border-b border-white/10 pb-2">
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="text-left hover:text-white transition-colors min-w-0"
                      >
                        {t.page.table.name} {renderSortIndicator("name")}
                      </button>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                        <div className="w-[24px]"></div>
                        <button
                          type="button"
                          onClick={() => toggleSort("av_fx_pts")}
                          className="text-right text-[#5DFDCB] hover:text-white transition-colors min-w-[50px]"
                        >
                          {t.page.table.fxAvg} {renderSortIndicator("av_fx_pts")}
                        </button>
                      </div>
                    </div>

                    {sortedPlayers.map((player, index) => (
                      <div
                        key={`${player.athlete_shortname}-${index}`}
                        className="border-b border-white/5 py-3 sm:py-4"
                      >
                        {/* Desktop/Tablet Row */}
                        <div className="hidden sm:grid grid-cols-[32px_24px_1fr_auto_56px_56px] gap-1.5 items-center">
                          <div className="flex items-center justify-center">
                            {player.picture ? (
                              <img
                                src={player.picture}
                                alt={player.athlete_shortname || "Player"}
                                className="w-7 h-7 sm:w-9 sm:h-9 rounded-full object-cover border border-[#5DFDCB]/30"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/10 border border-[#5DFDCB]/20" />
                            )}
                          </div>
                          <div className="flex items-center justify-center">
                            <span
                              className={`w-[20px] h-[20px] sm:w-[24px] sm:h-[24px] rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                            >
                              {getRoleInitial(player)}
                            </span>
                          </div>
                          <div className="text-white font-display font-black uppercase tracking-wide text-xs sm:text-sm truncate">
                            {player.athlete_shortname || "-"}
                          </div>
                          <div className="text-white/70 font-sans text-[11px] sm:text-sm truncate">
                            {getTeamText(player)}
                          </div>
                          <div className="text-white/70 font-sans text-right text-[11px] sm:text-sm">
                            {player.apps ?? "-"}
                          </div>
                          <div className="text-white font-display font-bold text-right text-[11px] sm:text-sm">
                            {formatFxAvg(player.av_fx_pts)}
                          </div>
                        </div>
                        {/* Mobile Row */}
                        <div className="flex sm:hidden items-center gap-3">
                          <div className="flex items-center gap-2 min-w-0" style={{flex: "1 1 0"}}>
                            {player.picture ? (
                              <img
                                src={player.picture}
                                alt={player.athlete_shortname || "Player"}
                                className="w-9 h-9 rounded-full object-cover border border-[#5DFDCB]/30 flex-shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-white/10 border border-[#5DFDCB]/20 flex-shrink-0" />
                            )}
                            <div className="min-w-0" style={{flex: "1 1 0"}}>
                              <div className="text-white font-display font-normal uppercase tracking-wide truncate text-[14px] leading-tight">
                                {player.athlete_shortname || "-"}
                              </div>
                              <div className="text-white/50 font-sans text-[11px] truncate">
                                {getTeamText(player)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" style={{flex: "0 0 auto", minWidth: "80px"}}>
                            <span
                              className={`w-[24px] h-[24px] rounded-full flex items-center justify-center text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                            >
                              {getRoleInitial(player)}
                            </span>
                            <div className="text-white font-display font-bold text-right text-[15px]" style={{width: "50px"}}>
                              {formatFxAvg(player.av_fx_pts)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {sortedPlayers.length === 0 && (
                      <p className="text-white/60 font-sans text-center py-10">
                        {lang === "it" ? "Nessun risultato." : "No results."}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {false && (
                <div className="overflow-x-auto">
                  <div className="min-w-[380px]">
                    <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[12px] text-amber-200 font-sans">
                      {lang === "it"
                        ? "Abbiamo problemi con la connessione ai dati, riprova piu tardi. Questi sono dati di test."
                        : "We are having trouble connecting to live data, please try again later. These are test data."}
                    </div>
                    {/* Desktop/Tablet Header */}
                    <div className="hidden sm:grid grid-cols-[32px_24px_1fr_auto_56px_56px] gap-1.5 text-[11px] sm:text-xs font-display font-bold uppercase text-white/60 border-b border-white/10 pb-2">
                      <div />
                      <div />
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="text-left hover:text-white transition-colors"
                      >
                        {t.page.table.name} {renderSortIndicator("name")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("team")}
                        className="text-left hover:text-white transition-colors"
                      >
                        {t.page.table.team} {renderSortIndicator("team")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("apps")}
                        className="text-right hover:text-white transition-colors"
                      >
                        {t.page.table.apps} {renderSortIndicator("apps")}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSort("av_fx_pts")}
                        className="text-right text-[#5DFDCB] hover:text-white transition-colors"
                      >
                        {t.page.table.fxAvg} {renderSortIndicator("av_fx_pts")}
                      </button>
                    </div>
                    {/* Mobile Header */}
                    <div className="flex sm:hidden items-center text-[13px] font-display font-bold uppercase text-white/60 border-b border-white/10 pb-2">
                      <button
                        type="button"
                        onClick={() => toggleSort("name")}
                        className="text-left hover:text-white transition-colors min-w-0"
                      >
                        {t.page.table.name} {renderSortIndicator("name")}
                      </button>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                        <div className="w-[24px]"></div>
                        <button
                          type="button"
                          onClick={() => toggleSort("av_fx_pts")}
                          className="text-right text-[#5DFDCB] hover:text-white transition-colors min-w-[50px]"
                        >
                          {t.page.table.fxAvg} {renderSortIndicator("av_fx_pts")}
                        </button>
                      </div>
                    </div>

                    {sortedPlayers.map((player, index) => (
                      <div
                        key={`${player.athlete_shortname}-${index}`}
                        className="border-b border-white/5 py-3 sm:py-4"
                      >
                        {/* Desktop/Tablet Row */}
                        <div className="hidden sm:grid grid-cols-[32px_24px_1fr_auto_56px_56px] gap-1.5 items-center">
                          <div className="flex items-center justify-center">
                            {player.picture ? (
                              <img
                                src={player.picture}
                                alt={player.athlete_shortname || "Player"}
                                className="w-7 h-7 sm:w-9 sm:h-9 rounded-full object-cover border border-[#5DFDCB]/30"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-white/10 border border-[#5DFDCB]/20" />
                            )}
                          </div>
                          <div className="flex items-center justify-center">
                            <span
                              className={`w-[20px] h-[20px] sm:w-[24px] sm:h-[24px] rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                            >
                              {getRoleInitial(player)}
                            </span>
                          </div>
                          <div className="text-white font-display font-black uppercase tracking-wide text-xs sm:text-sm truncate">
                            {player.athlete_shortname || "-"}
                          </div>
                          <div className="text-white/70 font-sans text-[11px] sm:text-sm truncate">
                            {getTeamText(player)}
                          </div>
                          <div className="text-white/70 font-sans text-right text-[11px] sm:text-sm">
                            {player.apps ?? "-"}
                          </div>
                          <div className="text-white font-display font-bold text-right text-[11px] sm:text-sm">
                            {formatFxAvg(player.av_fx_pts)}
                          </div>
                        </div>
                        {/* Mobile Row */}
                        <div className="flex sm:hidden items-center gap-3">
                          <div className="flex items-center gap-2 min-w-0" style={{flex: "1 1 0"}}>
                            {player.picture ? (
                              <img
                                src={player.picture}
                                alt={player.athlete_shortname || "Player"}
                                className="w-9 h-9 rounded-full object-cover border border-[#5DFDCB]/30 flex-shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-white/10 border border-[#5DFDCB]/20 flex-shrink-0" />
                            )}
                            <div className="min-w-0" style={{flex: "1 1 0"}}>
                              <div className="text-white font-display font-normal uppercase tracking-wide truncate text-[14px] leading-tight">
                                {player.athlete_shortname || "-"}
                              </div>
                              <div className="text-white/50 font-sans text-[11px] truncate">
                                {getTeamText(player)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" style={{flex: "0 0 auto", minWidth: "80px"}}>
                            <span
                              className={`w-[24px] h-[24px] rounded-full flex items-center justify-center text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                            >
                              {getRoleInitial(player)}
                            </span>
                            <div className="text-white font-display font-bold text-right text-[15px]" style={{width: "50px"}}>
                              {formatFxAvg(player.av_fx_pts)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {sortedPlayers.length === 0 && (
                      <p className="text-white/60 font-sans text-center py-10">
                        {lang === "it" ? "Nessun risultato." : "No results."}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <Toaster position="bottom-center" theme="dark" />
      </div>
    </>
  );
};

export default TopPlayersPage;
